import { registerLocaleVariant } from "../registry";
import { makeId } from "../helpers";
import type { ArticleBody } from "../types";

// ─── Helper ─────────────────────────────────────────────────────────

function frArticle(
  slug: string,
  variant: {
    title: string;
    subtitle: string;
    body: Partial<ArticleBody> & Pick<ArticleBody, "sections">;
  },
): void {
  registerLocaleVariant(makeId(slug), "fr", variant);
}

// ─── Article 1: Male Factor Infertility ─────────────────────────────

frArticle("new-article-male-factor", {
  title: "Le facteur masculin : ce que les chiffres veulent dire",
  subtitle: "Comprendre son côté de l'équation — et la suite à donner.",
  body: {
    quickSummary: [
      {
        title: "C'est la moitié du tableau.",
        description:
          "Le facteur masculin contribue à environ 40 à 50 % de tous les cas d'infertilité, et pourtant c'est souvent la dernière chose testée.",
      },
      {
        title: "Un examen dit beaucoup.",
        description:
          "Un spermogramme mesure la numération, la mobilité, et la morphologie — les trois piliers de la santé spermatique.",
      },
      {
        title: "La plupart des cas sont traitables.",
        description:
          "Des changements d'hygiène de vie à la réparation chirurgicale, la majorité des problèmes de facteur masculin ont des solutions efficaces.",
      },
    ],
    sections: [
      {
        title: "Qu'est-ce que l'infertilité de facteur masculin ?",
        content:
          "L'infertilité de facteur masculin signifie que quelque chose dans le sperme rend la conception plus difficile. Il peut y avoir trop peu de spermatozoïdes, ils peuvent mal nager, ou avoir une forme qui rend la fécondation difficile. Environ 40 à 50 % des cas d'infertilité impliquent un facteur masculin, seul ou en plus d'un facteur féminin. Malgré sa fréquence, beaucoup de couples passent des mois ou des années à n'investiguer que la partenaire féminine avant qu'un spermogramme soit prescrit. Se faire tester tôt fait économiser du temps, de l'argent, et des chagrins.",
        bulletPoints: [
          "Touche environ 1 couple sur 8 essayant de concevoir",
          "Peut être la seule cause ou un facteur contributif aux problèmes féminins",
          "N'a souvent aucun symptôme évident — la plupart des hommes se sentent en pleine santé",
          "Un simple spermogramme est la première et plus importante étape",
        ],
      },
      {
        title: "Le spermogramme expliqué",
        content:
          "Le spermogramme est l'examen clé pour évaluer la fertilité masculine. Il est non invasif et pris en charge par la Sécurité sociale dans le cadre d'un bilan d'infertilité prescrit. L'échantillon est recueilli par éjaculation (généralement au laboratoire ou à la maison avec un kit spécial) et examiné dans l'heure. Le laboratoire évalue plusieurs paramètres, mais trois comptent le plus : la concentration de spermatozoïdes, la mobilité (mouvement), et la morphologie (forme). L'OMS définit des valeurs de référence, mais ton médecin de la reproduction interprétera tes résultats dans leur contexte.",
        bulletPoints: [
          "Concentration : au moins 15 millions de spermatozoïdes par millilitre est considéré normal",
          "Mobilité totale : 40 % ou plus des spermatozoïdes doivent bouger",
          "Mobilité progressive : 32 % ou plus doivent avancer efficacement",
          "Morphologie (critères stricts) : 4 % ou plus de formes normales est dans la norme",
          "Volume : 1,5 mL ou plus par éjaculat",
        ],
      },
      {
        title: "Causes courantes du facteur masculin",
        content:
          "Il y a beaucoup de raisons pour que les paramètres spermatiques ne soient pas optimaux. Certaines sont structurelles, d'autres hormonales, d'autres liées au mode de vie. Une varicocèle — veine dilatée dans le scrotum — est la cause corrigeable la plus courante et se retrouve chez environ 35 à 40 % des hommes avec infertilité primaire. Les déséquilibres hormonaux, les infections antérieures, les conditions génétiques, et certains médicaments peuvent aussi jouer un rôle. L'exposition à la chaleur (bains chauds, ordinateurs portables sur les genoux, vêtements serrés) et des facteurs de mode de vie comme le tabac, l'alcool excessif, et l'obésité sont des contributeurs bien documentés.",
        bulletPoints: [
          "Varicocèle : cause corrigeable la plus courante, présente dans 35 à 40 % des cas",
          "Problèmes hormonaux : testostérone basse, prolactine élevée, ou problèmes thyroïdiens",
          "Mode de vie : tabac, alcool excessif, obésité, exposition à la chaleur",
          "Médicaments : supplémentation en testostérone, certains antidépresseurs, et stéroïdes anabolisants",
          "Facteurs génétiques : microdélétions du chromosome Y, syndrome de Klinefelter",
        ],
      },
      {
        title: "Quand consulter un andrologue",
        content:
          "Si un spermogramme revient anormal, l'étape suivante est généralement une visite chez un andrologue ou un urologue spécialisé en médecine de la reproduction — un·e spécialiste qui se concentre sur la fertilité masculine. En France, ces consultations existent souvent au sein des CECOS ou des centres d'AMP. Le spécialiste fera un examen physique, revoira ton historique médical et de médicaments, et pourra prescrire des analyses sanguines (FSH, LH, testostérone, prolactine). Il ou elle pourra aussi recommander une échographie scrotale pour vérifier la présence de varicocèles ou d'autres problèmes structurels. Si le premier spermogramme est anormal, la pratique standard est de le répéter environ 3 mois plus tard — le temps d'un cycle complet de spermatogenèse (production ~74 jours + transit épididymaire 10 à 14 jours) — car les résultats peuvent fluctuer.",
        bulletPoints: [
          "Cherche un andrologue ou un·e urologue spécialisé·e en médecine de la reproduction, pas un·e urologue généraliste",
          "Attends-toi à un examen physique, un bilan hormonal, et possiblement une échographie scrotale",
          "Un seul résultat anormal doit toujours être confirmé par un test répété, environ 3 mois plus tard",
          "Apporte une liste complète de tes médicaments — certains courants altèrent la production spermatique",
          "En France, la liste des CECOS et de leurs consultations andrologiques est disponible sur cecos.org",
        ],
      },
      {
        title: "Options de traitement",
        content:
          "Le traitement dépend entièrement de la cause. La réparation d'une varicocèle (varicocélectomie) peut améliorer les paramètres spermatiques chez une majorité d'hommes opérés ; en revanche l'effet sur les taux de grossesse spontanée reste plus variable selon les méta-analyses Cochrane. Les déséquilibres hormonaux peuvent souvent être corrigés avec des médicaments comme le citrate de clomifène ou l'anastrozole (preuves modérées, prescription spécialisée). Les modifications du mode de vie — perdre du poids, arrêter le tabac, réduire l'alcool, et éviter l'exposition à la chaleur — peuvent améliorer les chiffres en 3 à 6 mois. Quand la conception naturelle reste difficile, la procréation assistée offre des options puissantes. L'IIU peut aider en cas de facteur masculin léger, tandis que la FIV avec injection intracytoplasmique de spermatozoïde (ICSI) peut obtenir une fécondation même avec une numération très basse ou une mobilité faible. En cas d'azoospermie obstructive (pas de spermatozoïdes dans l'éjaculat à cause d'un blocage) ou non obstructive, des procédures de prélèvement chirurgical comme la TESE ou la micro-TESE peuvent trouver des spermatozoïdes directement dans le testicule.",
        bulletPoints: [
          "Varicocélectomie : amélioration des paramètres spermatiques fréquente, effet sur le taux de grossesse spontanée plus modeste",
          "Le citrate de clomifène peut stimuler la production spermatique en cas de déséquilibre hormonal",
          "Les changements d'hygiène de vie montrent des améliorations en 3 à 6 mois",
          "L'ICSI permet la fécondation avec un seul spermatozoïde en bonne santé",
          "Le prélèvement chirurgical (TESE/micro-TESE) est une option pour l'azoospermie",
        ],
      },
      {
        title: "Le côté émotionnel chez les hommes",
        content:
          "L'infertilité de facteur masculin peut frapper fort émotionnellement, et les hommes souffrent souvent en silence. Il existe une attente culturelle selon laquelle la fertilité serait liée à la masculinité, ce qui peut rendre un diagnostic très personnel — voire honteux. Beaucoup d'hommes rapportent se sentir coupables d'être la raison pour laquelle le couple ne peut pas concevoir, ou impuissants parce qu'ils ne peuvent pas résoudre le problème rapidement. Il est important de savoir que l'infertilité de facteur masculin est une condition médicale, pas un reflet de qui tu es. Parler ouvertement à ta partenaire, chercher le soutien d'un thérapeute spécialisé en fertilité, ou rejoindre un groupe de soutien fertilité masculine peut faire une vraie différence. Tu n'as pas à porter ça tout seul.",
        bulletPoints: [
          "Les sentiments de culpabilité, honte, et inadéquation sont courants — et totalement normaux",
          "La fertilité est une question médicale, pas une mesure de la masculinité",
          "Une thérapie de couple ou individuelle avec un spécialiste de la fertilité aide",
          "Des communautés en ligne et des groupes de soutien existent spécifiquement pour les hommes",
          "Une communication ouverte avec ta partenaire renforce à la fois la relation et le processus",
        ],
      },
    ],
    references: [
      {
        title:
          "WHO laboratory manual for the examination and processing of human semen, 6th edition",
        authors: "World Health Organization",
        year: 2021,
        url: "https://www.who.int/publications/i/item/9789240030787",
        evidenceLevel: "guideline",
      },
      {
        title:
          "Diagnostic evaluation of the infertile male: a committee opinion",
        authors: "Practice Committee of the ASRM",
        journal: "Fertility and Sterility",
        year: 2021,
        url: "https://www.asrm.org/practice-guidance/practice-committee-documents/diagnostic-evaluation-of-the-infertile-male-a-committee-opinion-2021/",
        evidenceLevel: "guideline",
      },
      {
        title:
          "EAU Guidelines on Sexual and Reproductive Health (male infertility, varicocele)",
        authors: "European Association of Urology",
        year: 2024,
        url: "https://uroweb.org/guidelines/sexual-and-reproductive-health",
        evidenceLevel: "guideline",
      },
      {
        title:
          "Surgical or radiological treatment for varicoceles in subfertile men",
        authors: "Persad E, O'Loughlin CAA, Kaur S, et al.",
        journal: "Cochrane Database of Systematic Reviews",
        year: 2021,
        url: "https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD000479.pub6/full",
        evidenceLevel: "meta-analysis",
      },
      {
        title:
          "World Health Organization reference values for human semen characteristics",
        authors: "Cooper TG, Noonan E, von Eckardstein S, et al.",
        journal: "Human Reproduction Update",
        year: 2010,
        url: "https://academic.oup.com/humupd/article/16/3/231/639175",
        evidenceLevel: "cohort",
      },
      {
        title: "Réseau des CECOS : annuaire et consultations andrologiques",
        authors: "Fédération française des CECOS",
        url: "https://www.cecos.org/",
        evidenceLevel: "official",
      },
    ],
  },
});

// ─── Article 2: Recurrent Miscarriage ───────────────────────────────

frArticle("new-article-recurrent-miscarriage", {
  title: "Fausses couches à répétition : quand la perte se répète",
  subtitle: "Comprendre pourquoi ça arrive et ce qu'on peut faire.",
  body: {
    quickSummary: [
      {
        title: "Plus courant que tu ne le penses.",
        description:
          "Environ 1 à 2 % des couples vivent deux pertes de grossesse ou plus, et une cause est identifiée dans environ la moitié des cas.",
      },
      {
        title: "Les examens peuvent apporter des réponses.",
        description:
          "Un bilan complet incluant caryotype, évaluation utérine, et bilans sanguins peut révéler des causes traitables.",
      },
      {
        title: "L'espoir est réel.",
        description:
          "Même après trois pertes sans cause identifiée, la chance de réussite à la prochaine grossesse est encore de 60 à 75 %.",
      },
    ],
    sections: [
      {
        title: "Qu'est-ce qu'on appelle fausse couche à répétition ?",
        content:
          "La fausse couche à répétition (FCR) se définit traditionnellement comme deux pertes de grossesse cliniques ou plus avant 20 semaines de gestation. L'ASRM a mis à jour la définition en 2020 pour recommander une évaluation après deux pertes plutôt que d'attendre trois, qui était l'ancien standard. C'est important parce que chaque perte a un coût physique et émotionnel, et une investigation plus tôt signifie des réponses plus tôt. Il est utile de savoir qu'une fausse couche unique est extrêmement courante — elle survient dans 15 à 25 % des grossesses reconnues — et est généralement due à une erreur chromosomique aléatoire de l'embryon. Les pertes à répétition, par contre, suggèrent qu'il peut y avoir un facteur sous-jacent à investiguer.",
        bulletPoints: [
          "Définie comme deux pertes de grossesse ou plus avant 20 semaines",
          "L'ASRM recommande une évaluation après deux pertes, pas trois",
          "Une fausse couche unique est courante (15 à 25 % des grossesses) et généralement aléatoire",
          "Les pertes à répétition touchent environ 1 à 2 % des couples",
        ],
      },
      {
        title: "Causes possibles",
        content:
          "Les causes des fausses couches à répétition se regroupent en plusieurs catégories, et plusieurs facteurs peuvent parfois être en jeu. Les anomalies chromosomiques de l'embryon sont la cause la plus courante à tout âge, et deviennent plus fréquentes à mesure que la qualité ovocytaire décline avec l'âge. Les problèmes structurels utérins — comme un septum, des fibromes, ou des polypes — peuvent empêcher une bonne implantation ou croissance. Des facteurs hormonaux comme une maladie thyroïdienne non contrôlée, un diabète non géré, ou une insuffisance en progestérone peuvent aussi contribuer. Le syndrome des antiphospholipides (SAPL), une condition auto-immune qui cause des troubles de la coagulation, est l'une des causes traitables les plus importantes et se retrouve chez environ 15 % des femmes avec FCR. D'autres facteurs immunitaires, des thrombophilies, et des facteurs de mode de vie (tabac, alcool excessif, obésité importante) complètent le tableau.",
        bulletPoints: [
          "Chromosomique : erreurs embryonnaires aléatoires ou translocations équilibrées parentales",
          "Utérine : septum, fibromes, polypes, ou syndrome d'Asherman (adhérences)",
          "Hormonale : dysfonction thyroïdienne, diabète non contrôlé, insuffisance en progestérone",
          "Immunologique : syndrome des antiphospholipides (présent dans environ 15 % des cas de FCR)",
          "Thrombophilies : facteur V Leiden, mutation du gène de la prothrombine",
        ],
      },
      {
        title: "Le bilan d'examens",
        content:
          "Un bilan complet de FCR vise à identifier toute cause traitable. Ton médecin commencera probablement par des prises de sang pour les deux partenaires, incluant un caryotype (analyse des chromosomes) pour rechercher des translocations équilibrées — réarrangements structurels de chromosomes qui n'affectent pas la santé du porteur mais peuvent faire que les embryons aient des chromosomes anormaux. Tu seras aussi testée pour les anticorps antiphospholipides (anticoagulant lupique, anticardiolipine, et bêta-2 glycoprotéine), la fonction thyroïdienne, et possiblement un bilan de thrombophilie. L'imagerie de l'utérus est importante aussi — une hystérosonographie, une hystéroscopie, ou une IRM peut révéler des anomalies structurelles potentiellement corrigeables. Si possible, les tests génétiques sur les tissus de fausse couche peuvent déterminer si la perte était due à un problème chromosomique de cette grossesse spécifique.",
        bulletPoints: [
          "Caryotype pour les deux partenaires pour rechercher des translocations équilibrées",
          "Bilan d'anticorps antiphospholipides (testé deux fois, à 12 semaines d'intervalle)",
          "Bilan thyroïdien (TSH, T4 libre) et hémoglobine glyquée A1c",
          "Imagerie utérine : hystérosonographie, hystéroscopie, ou IRM",
          "Test génétique du tissu de fausse couche quand c'est possible",
        ],
      },
      {
        title: "Approches thérapeutiques",
        content:
          "Le traitement est adapté à la cause identifiée. Le SAPL est traité avec de l'aspirine à faible dose associée à de l'héparine de bas poids moléculaire pendant la grossesse ; cette combinaison peut environ doubler le taux de naissance vivante (méta-analyses Cochrane et données ESHRE 2022). Un septum utérin peut être corrigé chirurgicalement par hystéroscopie. La maladie thyroïdienne et le diabète sont gérés avec des médicaments pour optimiser les niveaux avant et pendant la grossesse. Si une translocation équilibrée est trouvée chez l'un des partenaires — situation rare (~2 à 4 % des FCR) — la FIV avec test génétique préimplantatoire pour réarrangements structurels (PGT-SR) peut sélectionner des embryons avec des chromosomes normaux. En France, le PGT-SR n'est autorisé que dans les centres CPDPN sur indication précise. La supplémentation en progestérone précoce a été évaluée dans deux grands essais (PROMISE 2015, PRISM 2019) : le bénéfice est limité globalement, mais devient significatif chez les femmes avec antécédent de trois pertes ou plus.",
        bulletPoints: [
          "SAPL : aspirine à faible dose + HBPM peut environ doubler le taux de naissance vivante (Cochrane Empson 2005, ESHRE 2022)",
          "Septum utérin : la correction par hystéroscopie est souvent simple",
          "Thyroïde/diabète : optimiser les niveaux avant de concevoir",
          "Translocation équilibrée parentale : rare (2 à 4 % des FCR) ; PGT-SR via CPDPN en France",
          "Progestérone : bénéfice significatif après trois pertes ou plus (PRISM, NEJM 2019)",
        ],
      },
      {
        title: "Le poids émotionnel et le deuil",
        content:
          "Les fausses couches à répétition font partie des expériences les plus dévastatrices émotionnellement en médecine de la reproduction. Chaque perte porte son propre deuil, et des pertes répétées peuvent s'accumuler en un poids qui semble insupportable. Beaucoup décrivent un sentiment d'isolement — les ami·e·s et la famille peuvent ne pas comprendre pourquoi tu continues à essayer, ou dire des choses bien intentionnées mais blessantes comme « au moins tu arrives à tomber enceinte ». L'anxiété pendant les grossesses suivantes est quasi universelle, chaque tiraillement, chaque passage aux toilettes, chaque échographie portant une peur intense. C'est une réponse normale à un traumatisme répété, pas un signe de faiblesse. Un soutien professionnel d'un thérapeute expérimenté en perte de grossesse, des groupes de soutien entre pair·e·s (comme ceux proposés par des organisations comme RESOLVE), et des conversations ouvertes avec ton partenaire sont tous des éléments importants de la guérison.",
        bulletPoints: [
          "Le deuil s'accumule avec chaque perte — on ne « s'habitue » pas",
          "L'anxiété pendant les grossesses suivantes est totalement normale",
          "Cherche un thérapeute spécialisé en perte de grossesse ou en traumatisme reproductif",
          "Des associations comme Petite Émilie et le Collectif BAMP offrent une communauté en France",
          "Les partenaires font leur deuil différemment — la communication est essentielle",
        ],
      },
      {
        title: "Réessayer après une perte",
        content:
          "Quand tu es prête à réessayer — et seulement quand tu es prête — la bonne nouvelle est que les chances sont souvent de ton côté. Même parmi les couples avec des pertes à répétition inexpliquées (aucune cause trouvée après un bilan complet), le taux de naissance vivante à la grossesse suivante est d'environ 60 à 75 %. Ton médecin peut recommander une approche de « soins attentifs » pour ta prochaine grossesse : suivi précoce et fréquent, échographies précoces pour rassurer, accès facile à ton équipe en cas de pic d'anxiété, et soutien en progestérone. Certaines cliniques proposent des programmes spécialisés pour pertes à répétition avec des infirmières dédiées et des points de contact plus fréquents. Il n'y a pas un seul bon timing pour réessayer. Certains couples veulent essayer immédiatement ; d'autres ont besoin de mois pour faire leur deuil et guérir. Les deux choix sont valides. Le plus important, c'est que tu te sentes soutenue et que les causes identifiées aient été traitées.",
        bulletPoints: [
          "60 à 75 % de naissance vivante même après pertes à répétition inexpliquées",
          "Un suivi précoce et des échographies fréquentes apportent du réconfort",
          "Il n'y a pas de « bon » timing — essaie quand tu te sens prête",
          "Assure-toi que les causes identifiées ont été traitées avant de concevoir",
          "Demande à ta clinique s'il existe des programmes spécialisés de pertes à répétition",
        ],
      },
    ],
    references: [
      {
        title: "ESHRE guideline: recurrent pregnancy loss: an update in 2022",
        authors: "Bender Atik R, Christiansen OB, Elson J, et al.",
        journal: "Human Reproduction Open",
        year: 2023,
        url: "https://academic.oup.com/hropen/article/2023/1/hoad002/7045426",
        evidenceLevel: "guideline",
      },
      {
        title:
          "Definitions of infertility and recurrent pregnancy loss: a committee opinion",
        authors: "Practice Committee of the ASRM",
        journal: "Fertility and Sterility",
        year: 2020,
        url: "https://www.asrm.org/practice-guidance/practice-committee-documents/definitions-of-infertility-and-recurrent-pregnancy-loss-a-committee-opinion-2020/",
        evidenceLevel: "guideline",
      },
      {
        title:
          "A Randomized Trial of Progesterone in Women with Recurrent Miscarriages (PROMISE)",
        authors: "Coomarasamy A, Williams H, Truchanowicz E, et al.",
        journal: "New England Journal of Medicine",
        year: 2015,
        url: "https://www.nejm.org/doi/full/10.1056/NEJMoa1504927",
        evidenceLevel: "rct",
      },
      {
        title:
          "A Randomized Trial of Progesterone in Women with Bleeding in Early Pregnancy (PRISM)",
        authors: "Coomarasamy A, Devall AJ, Cheed V, et al.",
        journal: "New England Journal of Medicine",
        year: 2019,
        url: "https://www.nejm.org/doi/full/10.1056/NEJMoa1813730",
        evidenceLevel: "rct",
      },
      {
        title:
          "Prevention of recurrent miscarriage for women with antiphospholipid antibody or lupus anticoagulant",
        authors: "Empson M, Lassere M, Craig J, Scott J.",
        journal: "Cochrane Database of Systematic Reviews",
        year: 2005,
        url: "https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD002859.pub2/full",
        evidenceLevel: "meta-analysis",
      },
      {
        title:
          "Recommandations pour la pratique clinique du CNGOF (pertes de grossesse)",
        authors: "Collège national des gynécologues et obstétriciens français",
        url: "https://www.cngof.fr/pratiques-cliniques/recommandations-pour-la-pratique-clinique",
        evidenceLevel: "guideline",
      },
      {
        title: "Association Petite Émilie — soutien deuil périnatal",
        authors: "Association Petite Émilie",
        url: "https://www.petiteemilie.org/",
        evidenceLevel: "reference",
      },
      {
        title: "Collectif BAMP — communauté patientes fertilité",
        authors: "Collectif BAMP",
        url: "https://www.bamp.fr/",
        evidenceLevel: "reference",
      },
    ],
  },
});

// ─── Article 3: Thyroid & Fertility ─────────────────────────────────

frArticle("new-article-thyroid-fertility", {
  title: "Thyroïde et fertilité : le lien caché",
  subtitle: "Une petite glande avec un grand impact sur la conception.",
  body: {
    quickSummary: [
      {
        title: "La thyroïde affecte chaque étape.",
        description:
          "De l'ovulation à l'implantation, à maintenir une grossesse, les hormones thyroïdiennes jouent un rôle à chaque étape.",
      },
      {
        title: "Une simple prise de sang.",
        description:
          "Vérifier la TSH et la T4 libre est rapide, peu coûteux, et peut révéler des problèmes très traitables.",
      },
      {
        title: "La cible de TSH a évolué.",
        description:
          "Les recommandations ATA 2017 ont assoupli la cible historique de 2,5 mUI/L pour la population générale en préconception ; ce seuil reste pertinent au 1er trimestre et chez les femmes déjà sous lévothyroxine ou anti-TPO+.",
      },
    ],
    sections: [
      {
        title: "Comment la thyroïde affecte la fertilité",
        content:
          "Ta thyroïde est une glande en forme de papillon à la base de ton cou, et elle donne essentiellement le rythme à tout ton métabolisme — incluant ton système reproductif. Les hormones thyroïdiennes (T3 et T4) influencent la régularité de ton cycle menstruel, la qualité de l'ovulation, la réceptivité de ton endomètre, et la capacité à maintenir une grossesse précoce. Quand les niveaux thyroïdiens sont déréglés — trop hauts ou trop bas — ça peut perturber l'un ou l'autre de ces processus. Les troubles thyroïdiens sont étonnamment courants chez les femmes en âge de procréer, touchant environ 2 à 4 % de la population, et la dysfonction thyroïdienne subclinique (légère) est encore plus fréquente.",
        bulletPoints: [
          "Les hormones thyroïdiennes régulent la régularité du cycle menstruel",
          "Elles influencent la qualité de l'ovulation et la production de progestérone",
          "La réceptivité de l'endomètre dépend en partie de la fonction thyroïdienne",
          "Environ 2 à 4 % des femmes en âge de procréer ont un trouble thyroïdien",
        ],
      },
      {
        title: "Hypothyroïdie vs hyperthyroïdie",
        content:
          "L'hypothyroïdie (thyroïde sous-active) est bien plus courante et plus directement liée aux problèmes de fertilité. Elle peut causer des règles irrégulières ou absentes, l'anovulation (échec de libérer un ovocyte), une insuffisance lutéale (où la deuxième moitié du cycle est trop courte pour l'implantation), et des niveaux élevés de prolactine qui suppriment davantage l'ovulation. La cause la plus courante est la thyroïdite de Hashimoto, une condition auto-immune. L'hyperthyroïdie (thyroïde sur-active), généralement due à la maladie de Basedow, est moins courante mais peut aussi causer des irrégularités menstruelles, des difficultés à concevoir, et un risque accru de fausse couche. Les deux conditions peuvent affecter la qualité ovocytaire indirectement en perturbant l'environnement hormonal nécessaire à un bon développement folliculaire.",
        bulletPoints: [
          "Hypothyroïdie : cycles irréguliers, anovulation, insuffisance lutéale",
          "La thyroïdite de Hashimoto est la cause la plus courante d'hypothyroïdie",
          "Hyperthyroïdie : irrégularité menstruelle et risque accru de fausse couche",
          "La maladie de Basedow est la cause la plus courante d'hyperthyroïdie",
          "Les deux conditions sont traitables et gérables pendant la conception",
        ],
      },
      {
        title: "Niveaux de TSH pour concevoir",
        content:
          "Les seuils ont évolué. Les recommandations ATA 2011 préconisaient une TSH < 2,5 mUI/L pour toutes les femmes essayant de concevoir ; les ATA 2017 ont assoupli cette position : la cible désormais recommandée en préconception pour la population générale est l'intervalle de référence du laboratoire (souvent ~0,4 à 4,0 mUI/L). Le seuil < 2,5 mUI/L reste pertinent dans plusieurs situations bien identifiées : femmes déjà sous lévothyroxine, anticorps anti-TPO positifs, FCR, ou au 1er trimestre de grossesse. Au-delà de 4 mUI/L, un traitement ou un avis spécialisé est habituellement recommandé. Si ta TSH est à 3,5 et que tu envisages une grossesse, c'est une zone à discuter avec ta médecin de la reproduction — pas une anomalie automatique.",
        bulletPoints: [
          "Plage de référence générale : ~0,4 à 4,0 mUI/L (selon ton laboratoire)",
          "Cible < 2,5 mUI/L : recommandée si tu es déjà traitée par lévothyroxine, anti-TPO+, ou enceinte (1er trimestre)",
          "TSH 2,5–4,0 hors traitement : à discuter avec ta médecin selon le contexte ; pas systématiquement à traiter",
          "TSH > 4,0 mUI/L : avis spécialisé et traitement à envisager",
          "Les anticorps anti-TPO sont un facteur clé : leur positivité justifie une cible plus stricte (Alexander 2017, ATA)",
        ],
      },
      {
        title: "Examens et traitement",
        content:
          "L'examen est simple : une prise de sang mesurant la TSH et la T4 libre au minimum, plus les anticorps anti-thyroperoxydase (anti-TPO) si on suspecte une Hashimoto. Ils peuvent être faits n'importe quel jour de ton cycle. Le traitement de l'hypothyroïdie est la lévothyroxine (Levothyrox, Synthroid), une hormone thyroïdienne synthétique prise quotidiennement à jeun. Les ajustements de dose sont faits toutes les 4 à 6 semaines selon des prises de sang répétées jusqu'à ce que la TSH atteigne la plage cible. La plupart des femmes ressentent les effets en quelques semaines. Pour l'hyperthyroïdie, le traitement est plus complexe et peut impliquer des antithyroïdiens (méthimazole ou PTU), avec une gestion attentive pendant la conception et la grossesse. Le point clé est que les problèmes thyroïdiens sont parmi les causes les plus traitables de problèmes de fertilité — une fois identifiés, la gestion est généralement simple et très efficace.",
        bulletPoints: [
          "Bilan de base : TSH, T4 libre, et anticorps anti-TPO",
          "La lévothyroxine est le traitement standard pour l'hypothyroïdie",
          "Ajustements de dose toutes les 4 à 6 semaines jusqu'à TSH optimisée",
          "Le test peut être fait n'importe quel jour du cycle menstruel",
          "Les effets du traitement se ressentent généralement en quelques semaines",
        ],
      },
      {
        title: "La thyroïde pendant la grossesse",
        content:
          "Optimiser ta thyroïde avant la grossesse est idéal, mais le suivi ne s'arrête pas une fois enceinte. Les besoins en hormone thyroïdienne augmentent d'environ 30 à 50 % pendant la grossesse, ce qui signifie que beaucoup de femmes qui étaient stables sous lévothyroxine avant la conception auront besoin d'une augmentation de dose — souvent dès 4 à 6 semaines de grossesse. Ta médecin devrait vérifier la TSH toutes les 4 semaines pendant le premier trimestre et au moins une fois par trimestre ensuite. Une hypothyroïdie non contrôlée pendant la grossesse est associée à la prééclampsie, le décollement placentaire, l'accouchement prématuré, et un développement neurologique fœtal altéré. La bonne nouvelle est qu'avec un suivi approprié et des ajustements de dose, les résultats pour les femmes avec une maladie thyroïdienne traitée sont excellents — essentiellement les mêmes que pour les femmes sans problème thyroïdien.",
        bulletPoints: [
          "Les besoins en hormone thyroïdienne augmentent de 30 à 50 % pendant la grossesse",
          "La dose de lévothyroxine doit souvent être augmentée vers 4 à 6 semaines de grossesse",
          "La TSH doit être vérifiée toutes les 4 semaines au premier trimestre",
          "Une hypothyroïdie non traitée comporte des risques pour la mère et le bébé",
          "Avec une bonne gestion, les résultats sont équivalents à la population générale",
        ],
      },
    ],
    references: [
      {
        title:
          "2017 Guidelines of the American Thyroid Association for the Diagnosis and Management of Thyroid Disease During Pregnancy and the Postpartum",
        authors: "Alexander EK, Pearce EN, Brent GA, et al.",
        journal: "Thyroid",
        year: 2017,
        url: "https://www.liebertpub.com/doi/10.1089/thy.2016.0457",
        evidenceLevel: "guideline",
      },
      {
        title:
          "Subclinical hypothyroidism in the infertile female population: a guideline",
        authors: "Practice Committee of the ASRM",
        journal: "Fertility and Sterility",
        year: 2015,
        url: "https://www.asrm.org/practice-guidance/practice-committee-documents/subclinical-hypothyroidism-in-the-infertile-female-population-a-guideline/",
        evidenceLevel: "guideline",
      },
      {
        title:
          "Treatment of Subclinical Hypothyroidism or Hypothyroxinemia in Pregnancy",
        authors: "Casey BM, Thom EA, Peaceman AM, et al.",
        journal: "New England Journal of Medicine",
        year: 2017,
        url: "https://www.nejm.org/doi/full/10.1056/NEJMoa1606205",
        evidenceLevel: "rct",
      },
      {
        title:
          "Levothyroxine treatment and pregnancy outcome in women with subclinical hypothyroidism undergoing assisted reproduction technologies",
        authors: "Velkeniers B, Van Meerhaeghe A, Poppe K, et al.",
        journal: "Human Reproduction Update",
        year: 2013,
        url: "https://academic.oup.com/humupd/article/19/3/251/776681",
        evidenceLevel: "meta-analysis",
      },
      {
        title:
          "Hypothyroïdies frustes chez l'adulte : diagnostic et prise en charge",
        authors: "Haute Autorité de Santé (HAS)",
        year: 2019,
        url: "https://www.has-sante.fr/jcms/c_606453/fr/hypothyroidies-frustes-chez-l-adulte-diagnostic-et-prise-en-charge",
        evidenceLevel: "guideline",
      },
      {
        title: "Médicaments à base de lévothyroxine — information patientes",
        authors: "Agence nationale de sécurité du médicament (ANSM)",
        url: "https://ansm.sante.fr/glossaire/levothyroxine",
        evidenceLevel: "official",
      },
    ],
  },
});

// ─── Article 4: FET Guide ───────────────────────────────────────────

frArticle("new-article-fet-guide", {
  title: "Ton guide du TEC : transfert d'embryon congelé",
  subtitle: "Tout ce que tu dois savoir sur ton jour de transfert.",
  body: {
    quickSummary: [
      {
        title: "Plus simple que la ponction.",
        description:
          "Un transfert d'embryon congelé est une procédure courte et indolore qui ne demande pas d'anesthésie ni de récupération importante.",
      },
      {
        title: "Deux options de cycle.",
        description:
          "Tu feras soit un cycle médicamenté (les hormones contrôlent le timing) soit un cycle naturel ou semi-naturel.",
      },
      {
        title: "De bons taux de réussite.",
        description:
          "Les taux de réussite du TEC sont comparables — et parfois meilleurs — que ceux du transfert frais dans la plupart des cliniques.",
      },
    ],
    sections: [
      {
        title: "Qu'est-ce qu'un transfert d'embryon congelé ?",
        content:
          "Un transfert d'embryon congelé (TEC) est le processus de décongélation d'un embryon précédemment congelé et son placement dans l'utérus. C'est l'étape finale dans l'utilisation d'embryons créés et congelés pendant un cycle de FIV. Le TEC est devenu l'approche préférée dans beaucoup de cliniques parce qu'il sépare la phase de stimulation et ponction (qui est dure pour ton corps) de la phase de transfert, donnant à ton utérus le temps de récupérer et à tes hormones le temps de se normaliser. La procédure elle-même est rapide — environ 10 à 15 minutes — et la plupart des patientes la décrivent comme similaire à un frottis en termes d'inconfort. Aucune anesthésie n'est nécessaire, et tu peux conduire pour rentrer.",
        bulletPoints: [
          "Décongélation et transfert d'un embryon précédemment congelé",
          "Sépare le stress de la ponction du transfert pour de meilleures conditions",
          "Procédure rapide : environ 10 à 15 minutes, sans anesthésie",
          "Les taux de survie après décongélation dépassent 95 % avec la vitrification moderne",
        ],
      },
      {
        title: "Cycle médicamenté vs cycle naturel",
        content:
          "Il y a deux approches principales pour préparer ton endomètre au transfert. Dans un cycle médicamenté (ou programmé), tu prends de l'œstrogène (comprimés, patchs, ou injections) en début de cycle pour épaissir l'endomètre, puis tu ajoutes de la progestérone (injections, ovules, ou les deux) à un moment précis pour le préparer à l'implantation. Le transfert est programmé selon le nombre de jours d'exposition à la progestérone. Dans un cycle TEC naturel, ton corps fait le travail hormonal seul — ta médecin surveille ton ovulation naturelle avec des échographies et prises de sang, puis programme le transfert selon ta montée naturelle de progestérone. Un cycle semi-naturel ajoute une injection de déclenchement pour contrôler le timing d'ovulation plus précisément. Chaque approche a des taux de réussite similaires, et ta médecin recommandera l'une selon la régularité de ton cycle, tes préférences, et ton historique médical.",
        bulletPoints: [
          "Médicamenté : l'œstrogène épaissit l'endomètre, la progestérone le prépare à l'implantation",
          "Naturel : les hormones propres de ton corps préparent l'endomètre, avec suivi",
          "Semi-naturel : cycle naturel plus injection de déclenchement pour un timing précis",
          "Les taux de réussite sont comparables entre les trois approches",
          "Les cycles médicamentés offrent plus de souplesse de planification",
        ],
      },
      {
        title: "La procédure de transfert",
        content:
          "Le jour du transfert est généralement calme et simple. Tu arriveras à la clinique avec la vessie pleine de manière confortable (le liquide aide à visualiser l'utérus à l'échographie). L'embryologiste décongèlera ton embryon ce matin-là et confirmera qu'il a survécu à la décongélation — tu auras souvent une photo. Ta médecin utilisera un fin cathéter souple passé à travers le col pour placer l'embryon dans l'utérus, guidée par échographie abdominale. Tu pourras voir le petit flash blanc à l'écran au moment où l'embryon est déposé. Le tout prend environ 10 à 15 minutes, et beaucoup de patientes disent que la vessie pleine était la partie la plus inconfortable. Ensuite, tu te reposeras quelques minutes avant de rentrer.",
        bulletPoints: [
          "Arrive avec une vessie confortablement pleine pour guider l'échographie",
          "L'embryon est décongelé ce matin-là — tu auras probablement une photo",
          "Un fin cathéter est guidé à travers le col par échographie",
          "La plupart des patientes décrivent un inconfort minimal",
          "Bref repos à la clinique, puis retour à la maison pour la journée",
        ],
      },
      {
        title: "L'attente de deux semaines",
        content:
          "Après le transfert, tu entres dans la fameuse attente de deux semaines — environ 9 à 12 jours avant ta prise de sang béta-hCG. C'est largement considéré comme la partie la plus dure de tout le processus de FIV. Tu continueras le soutien en progestérone (et œstrogène si en cycle médicamenté). Les restrictions d'activité varient selon les cliniques, mais la plupart des preuves modernes suggèrent qu'une activité normale est OK — pas besoin de repos strict au lit. Certaines patientes ont de légères crampes ou des saignements, ce qui peut absolument rien dire ou être un signe d'implantation. La vérité honnête, c'est que les symptômes pendant cette attente sont des indicateurs peu fiables dans un sens comme dans l'autre. Essaie de t'occuper, appuie-toi sur ton soutien, et sois douce avec toi-même. C'est OK d'être anxieuse.",
        bulletPoints: [
          "Prise de sang béta-hCG généralement 9 à 12 jours après le transfert",
          "Continue tous les médicaments prescrits pendant l'attente",
          "Une activité normale est généralement OK — le repos strict au lit n'est pas soutenu par les preuves",
          "Les symptômes pendant l'attente sont des indicateurs peu fiables",
          "C'est la période la plus dure émotionnellement — sois bienveillante avec toi-même",
        ],
      },
      {
        title: "Taux de réussite",
        content:
          "Les taux de réussite du TEC se sont nettement améliorés avec la technologie de vitrification. Pour les femmes de moins de 35 ans transférant un seul embryon testé PGT-A (chromosomes normaux), les taux d'implantation sont généralement de 60 à 70 %. Sans test PGT-A, les taux sont un peu plus bas car certains embryons transférés peuvent être chromosomiquement anormaux. L'âge au moment de la ponction (pas au moment du transfert) est le prédicteur le plus important. Pour les femmes de 35 à 37 ans à la ponction, les taux sont autour de 50 à 60 % par transfert avec un embryon testé. Pour 38 à 40 ans, environ 40 à 50 %. Ce sont des taux par transfert, donc la réussite cumulée sur plusieurs transferts est plus élevée. Ta clinique peut te fournir ses taux de réussite TEC spécifiques par tranche d'âge.",
        bulletPoints: [
          "Moins de 35 ans avec embryon testé PGT-A : 60 à 70 % par transfert",
          "35 à 37 ans avec embryon testé : 50 à 60 % par transfert",
          "38 à 40 ans avec embryon testé : 40 à 50 % par transfert",
          "L'âge à la ponction compte plus que l'âge au transfert",
          "Les taux cumulés sur plusieurs transferts sont significativement plus élevés",
        ],
      },
      {
        title: "Quand le TEC est préféré au transfert frais",
        content:
          "Il y a plusieurs situations où une approche « tout congeler » (congeler tous les embryons et faire un TEC plus tard) est recommandée plutôt qu'un transfert frais. Si tu es à risque de syndrome d'hyperstimulation ovarienne (SHO), congeler tous les embryons permet à ton corps de récupérer en sécurité. Si ta progestérone était élevée le jour du déclenchement, ton endomètre peut être moins réceptif — le TEC évite ce problème. Si tu fais un test PGT-A, les embryons doivent être congelés en attendant les résultats. Certaines recherches suggèrent aussi que l'environnement hormonal après stimulation peut être suboptimal pour l'implantation en général, donnant au TEC un léger avantage pour beaucoup de patientes. La stratégie « tout congeler » est devenue de plus en plus courante, avec beaucoup de cliniques à fort volume faisant maintenant plus de TEC que de transferts frais.",
        bulletPoints: [
          "Risque de SHO : congeler tous les embryons permet au corps de récupérer en sécurité",
          "Progestérone élevée au déclenchement : la réceptivité de l'endomètre peut être compromise",
          "Test PGT-A : les embryons doivent être congelés en attendant les résultats génétiques",
          "L'environnement hormonal post-stimulation peut réduire la réussite du transfert frais",
          "Beaucoup de cliniques majeures font maintenant plus de TEC que de transferts frais",
        ],
      },
    ],
    references: [
      {
        title:
          "Fresh versus elective frozen embryo transfer in IVF/ICSI cycles: a systematic review and meta-analysis of reproductive outcomes",
        authors: "Roque M, Haahr T, Geber S, Esteves SC, Humaidan P.",
        journal: "Human Reproduction Update",
        year: 2019,
        url: "https://academic.oup.com/humupd/article/25/1/2/5089735",
        evidenceLevel: "meta-analysis",
      },
      {
        title: "Transfer of Fresh versus Frozen Embryos in Ovulatory Women",
        authors: "Shi Y, Sun Y, Hao C, et al.",
        journal: "New England Journal of Medicine",
        year: 2018,
        url: "https://www.nejm.org/doi/full/10.1056/NEJMoa1705334",
        evidenceLevel: "rct",
      },
      {
        title:
          "Bed rest after embryo transfer negatively affects in vitro fertilization: a randomized controlled trial",
        authors: "Gaikwad S, Garrido N, Cobo A, Pellicer A, Remohi J.",
        journal: "Fertility and Sterility",
        year: 2013,
        url: "https://www.fertstert.org/article/S0015-0282(13)00646-1/fulltext",
        evidenceLevel: "rct",
      },
      {
        title: "ESHRE guideline: ovarian stimulation for IVF/ICSI",
        authors: "ESHRE Guideline Group on Ovarian Stimulation",
        journal: "Human Reproduction Open",
        year: 2020,
        url: "https://academic.oup.com/hropen/article/2020/2/hoaa009/5826933",
        evidenceLevel: "guideline",
      },
    ],
  },
});

// ─── Article 5: Embryo Transfer Guide ───────────────────────────────

frArticle("new-article-embryo-transfer-guide", {
  title: "Ton guide du transfert d'embryon",
  subtitle: "À quoi t'attendre avant, pendant et après le transfert.",
  body: {
    quickSummary: [
      {
        title: "Le geste est court.",
        description:
          "La plupart des transferts durent environ 10 à 15 minutes et ne nécessitent pas d'anesthésie.",
      },
      {
        title: "Les consignes de ta clinique priment.",
        description:
          "Suis les indications de la clinique pour la vessie, les médicaments et l'activité après le transfert.",
      },
      {
        title: "La béta donne la réponse.",
        description:
          "La prise de sang de béta hCG est la prochaine étape fiable ; les symptômes ne permettent pas de prédire le résultat.",
      },
    ],
    sections: [
      {
        title: "Avant le transfert",
        content:
          "Ta clinique te dira comment te préparer, notamment s'il faut arriver avec la vessie confortablement pleine et quels médicaments prendre. Note tes questions sur la progestérone, les œstrogènes, l'activité et la date de la prise de sang de béta.",
        bulletPoints: [
          "Suis les consignes de médicaments de la clinique",
          "Confirme l'heure d'arrivée et les consignes pour la vessie",
          "Demande quand et où aura lieu la prise de sang de béta hCG",
        ],
      },
      {
        title: "Le jour du transfert",
        content:
          "Le médecin guide un cathéter fin et souple à travers le col pour déposer l'embryon dans l'utérus, généralement avec une échographie. Le geste est rapide. Une légère pression ou des crampes peuvent survenir ; ta clinique t'expliquera ce qui est attendu dans ton cas.",
        bulletPoints: [
          "Le transfert dure généralement 10 à 15 minutes",
          "Il ne nécessite habituellement pas d'anesthésie",
          "Les consignes de ta clinique priment sur les conseils généraux",
        ],
      },
      {
        title: "Après le transfert",
        content:
          "Continue les médicaments prescrits exactement comme indiqué jusqu'à ce que la clinique modifie le plan. De légers saignements, des crampes ou l'absence totale de symptômes peuvent survenir pendant l'attente et ne prédisent pas le résultat. Contacte ta clinique en cas de symptôme urgent ou inhabituel selon ses consignes.",
        bulletPoints: [
          "Continue la progestérone et les autres traitements prescrits",
          "Garde la date de la béta visible",
          "Fie-toi à la béta plutôt qu'aux symptômes ou aux tests trop précoces",
        ],
      },
    ],
  },
});

// ─── Article 6: IUI Guide ───────────────────────────────────────────

frArticle("new-article-iui-guide", {
  title: "L'IIU : ta première option de traitement",
  subtitle: "Une première étape douce quand tu as besoin d'un peu d'aide.",
  body: {
    quickSummary: [
      {
        title: "Moins invasive que la FIV.",
        description:
          "L'IIU est une procédure plus simple et moins chère qui place du sperme lavé directement dans l'utérus autour de l'ovulation.",
      },
      {
        title: "Idéale pour des situations précises.",
        description:
          "L'IIU marche mieux en cas d'infertilité inexpliquée, de facteur masculin léger, ou avec du sperme de donneur.",
      },
      {
        title: "Savoir quand passer à autre chose.",
        description:
          "La plupart des médecins recommandent 3 à 4 cycles d'IIU avant d'envisager la FIV si l'IIU n'a pas marché.",
      },
    ],
    sections: [
      {
        title: "Qu'est-ce que l'IIU ?",
        content:
          "L'insémination intra-utérine (IIU) est un traitement de fertilité qui place du sperme concentré et lavé directement dans l'utérus à travers un fin cathéter, programmé pour coïncider avec l'ovulation. En contournant le col et en déposant le sperme plus près des trompes de Fallope, l'IIU donne aux spermatozoïdes une longueur d'avance. Le processus de « lavage » est clé — le labo sépare les spermatozoïdes les plus mobiles et en bonne santé du sperme, enlève le liquide séminal et autres débris, et les concentre dans un petit volume. Cela signifie que les meilleurs spermatozoïdes sont délivrés exactement là où ils doivent être. La procédure elle-même prend juste quelques minutes et ressemble à un frottis. La plupart des femmes ressentent peu ou pas de crampes.",
        bulletPoints: [
          "Sperme lavé et concentré placé directement dans l'utérus",
          "Programmé pour coïncider avec l'ovulation, maximisant les chances",
          "Le lavage sélectionne les spermatozoïdes les plus sains et mobiles",
          "Procédure rapide — juste quelques minutes, inconfort minimal",
          "Peut être fait avec ou sans médicaments de stimulation de l'ovulation",
        ],
      },
      {
        title: "À qui s'adresse l'IIU ?",
        content:
          "L'IIU est souvent le traitement de première intention dans plusieurs situations. Elle est couramment recommandée pour les couples avec une infertilité inexpliquée (quand tous les examens reviennent normaux mais la grossesse n'arrive pas), un facteur masculin léger (numération ou mobilité légèrement basses), des problèmes de facteur cervical (où la glaire cervicale peut être hostile au sperme), et pour les femmes célibataires ou couples de même sexe utilisant du sperme de donneur. L'IIU peut être faite en cycle naturel (programmée selon ton ovulation naturelle) ou avec des médicaments de stimulation comme le létrozole (Femara) ou le citrate de clomifène (Clomid), qui augmentent le nombre d'ovocytes libérés et améliorent le timing. Les cycles d'IIU médicamentés ont des taux de réussite plus élevés que les non médicamentés.",
        bulletPoints: [
          "Infertilité inexpliquée après un bilan de base complet",
          "Facteur masculin léger : numération ou mobilité légèrement basses",
          "Facteur cervical : problèmes de glaire qui peuvent bloquer le sperme",
          "Femmes célibataires ou couples de même sexe utilisant du sperme de donneur",
          "Troubles d'ovulation combinés à un médicament d'induction de l'ovulation",
        ],
      },
      {
        title: "La procédure étape par étape",
        content:
          "Un cycle d'IIU suit un schéma prévisible. Si tu fais un cycle médicamenté, tu commenceras le létrozole ou clomid en début de cycle (généralement jours 3 à 7). Vers le jour 10 à 12, tu auras une visite de suivi avec échographie pour vérifier le développement folliculaire et une prise de sang pour vérifier les niveaux hormonaux. Quand un follicule dominant atteint environ 18 à 20 mm, on peut te donner une injection de déclenchement (hCG) pour induire l'ovulation, ou ta médecin peut surveiller ton pic de LH naturel. L'IIU est programmée environ 24 à 36 heures après l'injection de déclenchement ou le pic de LH. Le jour de la procédure, ton partenaire produit un échantillon de sperme (ou un échantillon de donneur est décongelé), le labo le lave et le prépare (environ 30 à 60 minutes), puis ta médecin réalise l'insémination. Tu resteras allongée environ 10 minutes après, puis tu pourras reprendre ta journée normalement.",
        bulletPoints: [
          "Jours 3 à 7 : médicament oral (létrozole ou clomid) si cycle médicamenté",
          "Jour 10 à 12 : échographie de suivi et prise de sang",
          "Injection de déclenchement ou détection du pic de LH naturel",
          "IIU réalisée 24 à 36 heures après le déclenchement ou le pic",
          "Le lavage du sperme prend 30 à 60 minutes, l'insémination prend quelques minutes",
        ],
      },
      {
        title: "Taux de réussite et attentes réalistes",
        content:
          "Avoir des attentes réalistes sur l'IIU est important. Les taux de réussite par cycle pour l'IIU sont modestes : environ 10 à 15 % par cycle pour l'IIU médicamentée avec sperme du conjoint, et un peu plus (15 à 20 %) avec sperme de donneur (parce que le sperme de donneur est pré-sélectionné pour sa qualité). L'IIU non médicamentée a des taux plus bas, autour de 5 à 10 %. Ces chiffres peuvent paraître bas, mais ils sont par cycle — la réussite cumulée sur 3 à 4 cycles est significativement meilleure. Pour contexte, un couple en bonne santé essayant naturellement a environ 15 à 20 % de chance par mois, donc l'IIU médicamentée se rapproche des taux de fertilité naturelle. L'âge est le plus grand facteur : les taux de réussite sont les plus élevés pour les femmes de moins de 35 ans et déclinent nettement après 37 à 38 ans. En France, l'IIU est prise en charge à 100 % par la Sécurité sociale dans le cadre du parcours AMP, dans la limite de 6 inséminations remboursées. Aux États-Unis et dans la plupart des pays sans couverture publique, compte 500 à 4 000 $ par cycle.",
        bulletPoints: [
          "IIU médicamentée avec sperme conjoint : environ 10 à 15 % par cycle",
          "IIU médicamentée avec sperme donneur : environ 15 à 20 % par cycle",
          "IIU non médicamentée (cycle naturel) : environ 5 à 10 % par cycle",
          "La réussite cumulée sur 3 à 4 cycles est considérablement plus élevée",
          "Prise en charge en France : 100 % Sécu jusqu'à 6 IIU dans le parcours AMP",
        ],
      },
      {
        title: "Quand passer à la FIV",
        content:
          "La plupart des médecins de la reproduction recommandent d'essayer 3 à 4 cycles d'IIU avant de passer à la FIV, mais cela varie selon le diagnostic, l'âge, et les circonstances. Si tu as plus de 38 ans, ta médecin peut suggérer moins d'IIU ou aller directement en FIV à cause du facteur temps. L'IIU n'est pas non plus appropriée en cas de trompes de Fallope bouchées, de facteur masculin sévère, ou d'endométriose avancée — dans ces cas, la FIV est le meilleur point de départ. La décision de passer de l'IIU à la FIV peut paraître un grand pas, mais ce n'est pas un échec. L'IIU et la FIV sont des outils différents pour des situations différentes, et parfois l'approche plus douce ne suffit simplement pas. Les taux de réussite de la FIV par cycle sont nettement plus élevés, autour de 30 à 40 % de naissance vivante par transfert chez les femmes de moins de 35 ans selon le rapport médical et scientifique de l'Agence de la biomédecine. Pour beaucoup de patientes, passer à la FIV plus tôt est le chemin plus efficace vers leur objectif.",
        bulletPoints: [
          "La plupart des médecins recommandent 3 à 4 cycles d'IIU avant la FIV",
          "Plus de 38 ans : moins d'IIU peuvent être recommandées",
          "L'IIU n'est pas appropriée pour trompes bouchées, facteur masculin sévère, ou endo avancée",
          "Passer à la FIV n'est pas un échec — c'est choisir le bon outil pour la situation",
          "Les taux de réussite de la FIV sont nettement plus élevés par cycle que l'IIU",
        ],
      },
    ],
    references: [
      {
        title: "Intra-uterine insemination for unexplained subfertility",
        authors: "Veltman-Verhulst SM, Hughes E, Ayeleke RO, Cohlen BJ.",
        journal: "Cochrane Database of Systematic Reviews",
        year: 2016,
        url: "https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD001838.pub5/full",
        evidenceLevel: "meta-analysis",
      },
      {
        title: "Fertility problems: assessment and treatment (CG156)",
        authors: "NICE",
        year: 2017,
        url: "https://www.nice.org.uk/guidance/cg156",
        evidenceLevel: "guideline",
      },
      {
        title:
          "L'assistance médicale à la procréation (AMP) : ce qui est remboursé",
        authors: "Assurance Maladie (ameli.fr)",
        url: "https://www.ameli.fr/assure/remboursements/rembourse/assistance-medicale-la-procreation-amp/prise-en-charge-de-l-assistance-medicale-la-procreation-amp",
        evidenceLevel: "official",
      },
      {
        title:
          "Rapport médical et scientifique annuel — résultats de l'AMP en France",
        authors: "Agence de la biomédecine",
        url: "https://rams.agence-biomedecine.fr/",
        evidenceLevel: "official",
      },
    ],
  },
});

// ─── Article 7: Donor Eggs/Sperm ────────────────────────────────────

frArticle("new-article-donor-guide", {
  title: "Utiliser un don d'ovocytes ou de sperme en France",
  subtitle:
    "Le cadre français du don : anonymat, gratuité, et accès aux origines depuis 2021.",
  body: {
    introduction:
      "En France, le don de gamètes (ovocytes ou sperme) est encadré par la loi de bioéthique. C'est un système très différent des modèles privés américains : les dons sont anonymes au moment du recueil, gratuits (le donneur ne reçoit pas d'argent), et organisés via les CECOS (Centres d'étude et de conservation des œufs et du sperme humains). Depuis la loi de 2021, l'accès à l'AMP est ouvert aux femmes seules et aux couples de femmes, et les enfants nés de don ont désormais accès à l'identité du donneur à leurs 18 ans s'ils le demandent.",
    quickSummary: [
      {
        title: "Le don passe par les CECOS.",
        description:
          "En France, le don de gamètes est centralisé via le réseau public des CECOS. Pas de banque privée. Tu ne choisis pas le donneur — un appariement médical est fait par l'équipe.",
      },
      {
        title: "Anonyme à l'inscription, accessible à 18 ans.",
        description:
          "Depuis la loi de 2021, les enfants nés de don peuvent accéder à l'identité du donneur à leur majorité s'ils le souhaitent. Les donneurs s'engagent à cela à l'inscription.",
      },
      {
        title: "Pris en charge à 100 % par la Sécu.",
        description:
          "La FIV avec don d'ovocytes ou IIU avec don de sperme est remboursée à 100 % dans le cadre du parcours d'AMP conventionné, dans les mêmes limites d'âge et de cycles.",
      },
    ],
    sections: [
      {
        title: "Quand le don de gamètes est recommandé",
        content:
          "La décision d'utiliser des ovocytes ou du sperme de donneur arrive dans plusieurs situations. Le don d'ovocytes peut être recommandé aux femmes avec une réserve ovarienne très diminuée, une insuffisance ovarienne prématurée (IOP), des échecs répétés de FIV avec leurs propres ovocytes, porteuses de pathologies génétiques sérieuses, ou aux femmes proches de la limite d'âge. Le don de sperme est utilisé par les femmes seules, les couples de femmes, et les couples confrontés à un facteur masculin sévère (notamment une azoospermie sans possibilité de prélèvement chirurgical) ou à une pathologie génétique du côté masculin.",
        bulletPoints: [
          "Réserve ovarienne très diminuée ou insuffisance ovarienne prématurée",
          "Échecs répétés de FIV avec tes propres ovocytes",
          "Facteur masculin sévère ne répondant pas au traitement (azoospermie, par exemple)",
          "Pathologies génétiques transmissibles",
          "Femmes seules ou couples de femmes : le don de sperme fait partie du parcours dès le départ",
        ],
      },
      {
        title: "Le cadre légal français",
        content:
          "La loi de bioéthique (révisée en 2021) pose plusieurs principes forts qui structurent tout le don de gamètes en France. Comprendre ces principes t'aide à savoir à quoi t'attendre.",
        bulletPoints: [
          "Anonymat du donneur au moment du recueil : tu ne le rencontreras pas et n'auras pas accès à son identité pendant ton parcours",
          "Gratuité : le don est un acte volontaire, le donneur ne reçoit aucune compensation financière",
          "Accès aux origines : depuis 2021, l'enfant né de don peut demander à 18 ans l'identité du donneur via la CAPADD (Commission d'accès aux données des tiers donneurs)",
          "Pas de choix du donneur par la receveuse : un appariement médical et phénotypique est fait par l'équipe du CECOS",
          "Don dirigé interdit : tu ne peux pas désigner un donneur connu (ami, famille)",
          "GPA (gestation pour autrui) interdite en France",
        ],
      },
      {
        title: "Le parcours via le CECOS",
        content:
          "Le CECOS est le passage obligatoire pour un don de gamètes en France. C'est un réseau de centres publics rattachés aux hôpitaux universitaires. Une fois ton indication validée par ton équipe d'AMP, tu es orientée vers le CECOS le plus proche.",
        bulletPoints: [
          "Inscription au CECOS après indication médicale du centre d'AMP",
          "Entretiens médicaux et psychologiques obligatoires pour le couple ou la personne receveuse",
          "Délai d'attente : variable selon le type de don et la région — de quelques mois à 2 ans pour le don d'ovocytes",
          "Appariement médical : le CECOS sélectionne un donneur dont les caractéristiques médicales et phénotypiques sont compatibles",
          "Information sur l'origine du don : tu sais qu'il s'agit d'un don, mais pas qui",
        ],
      },
      {
        title: "Le délai d'attente",
        content:
          "C'est l'un des aspects les plus difficiles du parcours en France. Le don d'ovocytes est rare — environ 1 000 donneuses par an pour des milliers de receveuses — ce qui crée une vraie tension. Pour le don de sperme, l'attente est plus courte mais peut tout de même atteindre 6 à 12 mois. Certaines patientes choisissent pour cette raison un parcours à l'étranger (Espagne, Belgique, République tchèque) où le délai est très court (souvent quelques semaines). Mais le cadre légal de ces pays est différent du cadre français — vérifie notamment l'accès aux origines pour ton futur enfant.",
      },
      {
        title: "Le don d'ovocytes en pratique",
        content:
          "Quand un appariement est trouvé, ton cycle est synchronisé avec celui de la donneuse. La donneuse fait sa stimulation et sa ponction, et ses ovocytes sont fécondés avec le sperme de ton partenaire (ou d'un donneur). Les embryons obtenus sont cultivés jusqu'au stade blastocyste puis transférés ou congelés. Les taux de réussite sont parmi les plus élevés en AMP, car les donneuses sont jeunes (généralement entre 18 et 37 ans en France) et soigneusement sélectionnées.",
        bulletPoints: [
          "Synchronisation de ton cycle avec celui de la donneuse",
          "La donneuse fait sa stimulation et sa ponction au CECOS",
          "Ses ovocytes sont fécondés avec le sperme du conjoint ou d'un donneur",
          "Les embryons sont transférés à l'utérus de la receveuse",
          "Donneuses entre 18 et 37 ans (loi française), sélectionnées médicalement",
        ],
      },
      {
        title: "Le don de sperme en pratique",
        content:
          "Pour le don de sperme, les paillettes sont conservées au CECOS et délivrées à ton centre d'AMP pour ton cycle. Selon ton indication, tu peux faire une IIU (insémination intra-utérine) avec sperme de donneur — c'est l'approche la plus simple — ou une FIV avec sperme de donneur si une indication médicale supplémentaire le justifie. Les donneurs en France sont sélectionnés rigoureusement : examen médical, antécédents familiaux, tests infectieux et génétiques.",
        bulletPoints: [
          "Paillettes conservées au CECOS et délivrées à ton centre d'AMP",
          "IIU avec sperme de donneur : approche la plus simple et la plus courante",
          "FIV avec sperme de donneur : en cas d'indication supplémentaire",
          "Donneurs sélectionnés : examen médical, antécédents, tests infectieux et génétiques",
          "Limites légales : 10 enfants maximum issus du même donneur",
        ],
      },
      {
        title: "L'accès aux origines depuis 2021",
        content:
          "La loi de bioéthique de 2021 a profondément changé le cadre du don en France. Jusqu'alors, l'anonymat était total et définitif. Aujourd'hui, les donneurs s'engagent à ce que leur identité (ainsi que des données non identifiantes : description physique, situation familiale et professionnelle, motivations) puissent être communiquées à l'enfant né de don, à sa demande, à partir de ses 18 ans. C'est la CAPADD qui gère ces demandes. Pour les dons réalisés avant la loi, l'accès n'est possible que si le donneur historique l'accepte explicitement.",
      },
      {
        title: "Le parcours émotionnel et la révélation",
        content:
          "Utiliser des gamètes de donneur implique souvent un processus de deuil — faire le deuil du lien génétique avec ton enfant que tu pensais avoir. C'est une vraie perte, et elle mérite de la place et de la reconnaissance. Les CECOS proposent un accompagnement psychologique avant et pendant le parcours, et tu peux aussi consulter un·e thérapeute spécialisé·e en procréation par tiers. La question de la révélation à l'enfant — quand et comment lui dire — a beaucoup évolué en France avec la loi de 2021. Le consensus parmi les psychologues, éthicien·ne·s et adultes conçus par don est qu'une révélation précoce et adaptée à l'âge est meilleure pour le bien-être et le développement identitaire de l'enfant. Avec l'accès aux origines à 18 ans, le secret de famille n'est plus tenable durablement — la transparence devient une protection.",
        bulletPoints: [
          "Le deuil du lien génétique attendu est normal et valide",
          "L'accompagnement psychologique au CECOS est inclus dans le parcours",
          "Une révélation précoce et adaptée à l'âge est recommandée par les expert·e·s",
          "Avec l'accès aux origines à 18 ans, la transparence devient une vraie protection pour ton enfant",
          "Des associations comme l'ADEDD (Association des enfants du don) accompagnent les familles",
        ],
      },
      {
        title: "Et le parcours à l'étranger ?",
        content:
          "Beaucoup de couples et de femmes seules choisissent un parcours à l'étranger — Espagne et Belgique en tête — pour réduire les délais d'attente, accéder à un parcours plus rapide, ou pour des raisons d'âge. Légalement, tu peux le faire en tant que résidente française : il n'y a pas d'interdiction. Mais sache que :",
        bulletPoints: [
          "Le cadre légal du pays choisi peut différer : anonymat strict (Espagne avant 2023), accès aux origines (UK, Suède), choix du donneur (banques privées en Espagne)",
          "Aucune prise en charge française : à payer en intégralité",
          "Coordination médicale plus complexe : suivi à distance, déplacements",
          "Vérifie le statut légal de la parenté à ton retour en France pour ton enfant",
          "L'association MAIA et le Collectif BAMP peuvent t'orienter sur les centres à l'étranger les plus fiables",
        ],
      },
    ],
    references: [
      {
        title: "Loi n° 2021-1017 du 2 août 2021 relative à la bioéthique",
        authors: "Légifrance",
        year: 2021,
        url: "https://www.legifrance.gouv.fr/loda/id/JORFTEXT000043884384/",
        evidenceLevel: "official",
      },
      {
        title:
          "Don de gamètes : ovocytes, spermatozoïdes — cadre, parcours, accès aux origines",
        authors: "Agence de la biomédecine",
        url: "https://www.dondovocytes.fr/",
        evidenceLevel: "official",
      },
      {
        title: "Réseau des CECOS — informations donneurs et receveuses",
        authors: "Fédération française des CECOS",
        url: "https://www.cecos.org/",
        evidenceLevel: "official",
      },
      {
        title: "Commission d'accès aux données des tiers donneurs (CAPADD)",
        authors: "Ministère de la Santé",
        url: "https://solidarites.gouv.fr/capadd-commission-dacces-aux-donnees-des-tiers-donneurs",
        evidenceLevel: "official",
      },
      {
        title: "Collectif BAMP — communauté patientes AMP et dons",
        authors: "Collectif BAMP",
        url: "https://www.bamp.fr/",
        evidenceLevel: "reference",
      },
    ],
  },
});
